import React, { useEffect, useId, useRef, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import AspectRatioWrapper from '../AspectRatioWrapper/AspectRatioWrapper';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import IconArrowHead from '../IconArrowHead/IconArrowHead';

import css from './SwipeGallery.module.css';

// A slide counts as "the current one" once this much of it is visible in the scroller.
const VISIBILITY_THRESHOLD = 0.6;

const renderSlidesAsIs = slides => slides;

/**
 * A horizontally swipeable image gallery built on native CSS scroll-snap.
 *
 * There is deliberately no pointer/gesture handling: letting the browser own the scroll is
 * what gives us trackpad support, momentum and 1:1 finger tracking. Any JS drag
 * implementation loses all three.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.images API image entities
 * @param {Array<string>} props.variants variant names to request; each image is then narrowed
 * to the subset it actually has, so partially-processed images still degrade to the
 * ResponsiveImage placeholder instead of a blank <img>
 * @param {string?} props.sizes sizes attribute
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string?} props.title descriptive text (e.g. the listing title) included in every
 * slide's alt text, so screen-reader users hear what the image is of, not just its position
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {boolean?} props.showDots render a row of dot indicators below the gallery
 * @param {boolean?} props.showArrows render prev/next arrow buttons over the gallery
 * @param {Function?} props.onIndexChange called with the new active slide index whenever it
 * genuinely changes (not on mount, and not when the observer merely re-subscribes)
 * @param {Function?} props.renderSlides wraps the slides inside the scroller, e.g. in a link
 * @param {ReactNode?} props.children overlays drawn over the slides, e.g. a badge
 * @returns {JSX.Element}
 */
const SwipeGallery = props => {
  const {
    images: imagesProp,
    variants = [],
    sizes,
    aspectWidth = 1,
    aspectHeight = 1,
    title,
    className,
    rootClassName,
    showDots = false,
    showArrows = false,
    onIndexChange,
    renderSlides = renderSlidesAsIs,
    children,
  } = props;
  const intl = useIntl();
  const scrollerRef = useRef(null);
  const images = imagesProp ?? [];
  const count = images.length;
  // Identifies *which* images are showing, not just how many. A same-length replacement
  // list (e.g. a filter change upstream) still produces a different key here, because the
  // slides below are keyed by uuid and would otherwise remount without the effects below
  // noticing anything changed.
  const imagesKey = images.map((image, i) => image?.id?.uuid ?? i).join(',');
  const [index, setIndex] = useState(0);
  const [isFirstImageSettled, setIsFirstImageSettled] = useState(false);
  const settleFirstImage = () => setIsFirstImageSettled(true);
  // The slide an arrow was last pressed for. Its position is announced once the gallery lands
  // there, so swipes and new results stay silent.
  const [arrowTarget, setArrowTarget] = useState(null);
  const positionId = useId();

  // Kept in a ref so an inline arrow function from a consumer doesn't tear down and
  // rebuild the observer on every render.
  const onIndexChangeRef = useRef(onIndexChange);
  useEffect(() => {
    onIndexChangeRef.current = onIndexChange;
  });

  // IntersectionObserver delivers an initial batch as soon as it starts observing, and the
  // effect below re-subscribes whenever imagesKey changes -- neither is a real navigation.
  // Tracking what was last actually reported keeps onIndexChange firing only on a genuine
  // change, matching its contract. (Not folded into a setIndex updater: React 18
  // StrictMode double-invokes updaters in dev, which would double-fire it.)
  const lastReportedIndexRef = useRef(0);

  // A same-length image list is still a different set of slides (see imagesKey above), so
  // jump back to the first one rather than leaving the index pointing at whatever used to
  // be there. `behavior: 'instant'` sidesteps the CSS `scroll-behavior: smooth`, which
  // would otherwise animate a reset the user didn't ask for.
  useEffect(() => {
    setIndex(0);
    setArrowTarget(null);
    lastReportedIndexRef.current = 0;
    scrollerRef.current?.scrollTo({ left: 0, behavior: 'instant' });
  }, [imagesKey]);

  // The slides are server-rendered, so the first photo can finish before hydration attaches
  // onLoad, and that load event is never seen. A first slide with no usable variant renders
  // a placeholder instead of an <img>, which never loads at all.
  useEffect(() => {
    const firstImg = scrollerRef.current?.querySelector('[data-slide-index="0"] img');
    if (!firstImg || firstImg.complete) {
      settleFirstImage();
    }
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || count <= 1) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            return;
          }
          const next = Number(entry.target.dataset.slideIndex);
          if (Number.isNaN(next)) {
            return;
          }
          setIndex(next);
          if (lastReportedIndexRef.current !== next) {
            lastReportedIndexRef.current = next;
            onIndexChangeRef.current?.(next);
          }
        });
      },
      { root: scroller, threshold: VISIBILITY_THRESHOLD }
    );

    scroller.querySelectorAll('[data-slide-index]').forEach(slide => observer.observe(slide));

    return () => observer.disconnect();
    // imagesKey (not count) is deliberate: slides are keyed by uuid, so a same-length swap
    // remounts every slide and leaves this observing detached nodes unless it re-subscribes.
  }, [imagesKey]);

  // AspectRatioWrapper already contributes position/width to this element via its own
  // className handling; css.root stays unconditional (rather than rootClassName ||
  // css.root) because the hover-reveal arrow CSS below targets .root specifically, and a
  // consumer passing rootClassName must not be able to silently disable it.
  const classes = classNames(css.root, rootClassName, className);

  if (count === 0) {
    return (
      <AspectRatioWrapper className={classes} width={aspectWidth} height={aspectHeight}>
        <ResponsiveImage rootClassName={css.image} image={null} variants={[]} alt="" />
      </AspectRatioWrapper>
    );
  }

  const hasTitle = typeof title === 'string' && title.length > 0;
  const isFirst = index === 0;
  const isLast = index === count - 1;
  const imageLabel = i =>
    intl.formatMessage(
      { id: hasTitle ? 'SwipeGallery.imageLabel' : 'SwipeGallery.imageLabelNoTitle' },
      { title, index: i + 1, count }
    );

  // The scroller owns the animation via `scroll-behavior: smooth` in CSS, which also lets
  // prefers-reduced-motion turn it off without a JS branch. offsetLeft (rather than index *
  // clientWidth) stays exact regardless of clientWidth's integer rounding, and keeps working
  // if slide sizing ever stops being a uniform 100%.
  const scrollToIndex = target => {
    const scroller = scrollerRef.current;
    const slide = scroller?.querySelector(`[data-slide-index="${target}"]`);
    if (slide) {
      scroller.scrollTo({ left: slide.offsetLeft });
    }
  };

  const slides = images.map((image, i) => {
    // Narrow to the variants this particular image actually has. A shared variants
    // array isn't safe here: Sharetribe can return images still being processed with
    // a reduced variant set, and requesting a variant that doesn't exist yields an
    // empty srcSet (a blank <img>) instead of ResponsiveImage's own placeholder.
    const availableVariants = Object.keys(image?.attributes?.variants || {}).filter(k =>
      variants.includes(k)
    );
    const loadingPropsMaybe =
      i === 0 ? { onLoad: settleFirstImage, onError: settleFirstImage } : {};

    return (
      <div className={css.slide} key={image.id?.uuid || i} data-slide-index={i}>
        <ResponsiveImage
          rootClassName={css.image}
          image={image}
          variants={availableVariants}
          sizes={sizes}
          alt={imageLabel(i)}
          draggable={false}
          decoding="async"
          loading="lazy"
          {...loadingPropsMaybe}
        />
      </div>
    );
  });

  // Arrows use aria-disabled rather than `disabled`: a button that disables itself while
  // focused would drop focus to <body>. A disabled arrow leaves the tab order instead, so
  // tabbing in never lands on an arrow that is hidden.
  const arrow = (direction, target, isDisabled) => (
    <button
      type="button"
      className={classNames(css.arrow, direction === 'left' ? css.arrowLeft : css.arrowRight)}
      aria-disabled={isDisabled ? 'true' : undefined}
      tabIndex={isDisabled ? -1 : undefined}
      aria-label={intl.formatMessage({
        id: direction === 'left' ? 'SwipeGallery.previousImage' : 'SwipeGallery.nextImage',
      })}
      aria-describedby={positionId}
      onClick={() => {
        if (!isDisabled) {
          setArrowTarget(target);
          scrollToIndex(target);
        }
      }}
    >
      <IconArrowHead direction={direction} />
    </button>
  );

  return (
    <AspectRatioWrapper
      className={classNames(classes, { [css.shimmer]: !isFirstImageSettled })}
      width={aspectWidth}
      height={aspectHeight}
    >
      {/* Firefox and Chromium would otherwise make the scroller a tab stop of its own. */}
      <div className={css.scroller} ref={scrollerRef} tabIndex={-1}>
        {renderSlides(slides)}
      </div>

      {children}

      {count > 1 && showArrows ? (
        <>
          {arrow('left', index - 1, isFirst)}
          {arrow('right', index + 1, isLast)}
          <span id={positionId} hidden>
            {imageLabel(index)}
          </span>
          <span className={css.hidden} aria-live="polite" aria-atomic="true">
            {index === arrowTarget ? imageLabel(index) : ''}
          </span>
        </>
      ) : null}

      {count > 1 && showDots ? (
        <div className={css.dots} aria-hidden="true">
          {images.map((image, i) => (
            <span
              key={image.id?.uuid || i}
              className={classNames(css.dot, { [css.dotActive]: i === index })}
            />
          ))}
        </div>
      ) : null}
    </AspectRatioWrapper>
  );
};

export default SwipeGallery;
